import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Platform } from '@ionic/angular';
import { ReplaySubject } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';

import { UtilsService } from './utils.service';
import { ApiService } from './api.service';
import { StorageService } from './storage.service';
import * as _ from 'lodash';

// Google OAuth types
declare const google: any;

@Injectable({
  providedIn: 'root'
})
export class GoogleAuthService {

  // Web OAuth client, from the shared "TeamTools" Google project (424870364007). See README "Google Cloud projects & OAuth clients".
  private readonly G_CLIENT_ID: string = '424870364007-7okd1ejm2m6rmdvdktbeus15qg4pf8vf.apps.googleusercontent.com';
  private readonly G_CLIENT_SECRET: string = 'bNhdJw-DW305TtrlsqiPnC7q';
  private readonly G_USER_INFO_NAME: string = 'T2ID';

  // Scopes for basic profile access
  private readonly G_SCOPES_MINIMAL = 'profile email ';

  // Full Google Workspace admin scopes
  private readonly G_SCOPES = 
    'https://www.googleapis.com/auth/admin.directory.user ' + 
    'https://www.googleapis.com/auth/admin.directory.group ' +
    'https://www.googleapis.com/auth/apps.groups.settings ' +
    'https://www.googleapis.com/auth/admin.directory.userschema ' +
    // 'https://www.googleapis.com/auth/admin.directory.customer.readonly ' +
    // 'audience:server:client_id:424870364007-7okd1ejm2m6rmdvdktbeus15qg4pf8vf.apps.googleusercontent.com '+
    'profile ' + 
    'email ';

  public readonly NO_GMAIL_MSG = 'To use TeamTools, please sign in with your Google Workspace email';
  public readonly LOGIN_ERROR_MSG = 'TeamTools cannot reach the Google servers. Please contact TeamTools support.';

  // our authentication object storage, will be different for web vs native
  private gAuthObj: any;
  // google's auth service (web only) 
  private gAuthService: any;

  // User information properties from auth service
  public id: string = '';
  public email: string = '';
  public displayName: string = '';
  public lastName: string = '';
  public firstName: string = '';
  public imageUrl: string = '';
  public domain: string = '';
  public loggedInTime: number = 0;

  // Auth tokens (protected)
  private _idToken: string = '';
  get idToken(): string {
    return this._idToken;
  }

  private _accessToken: string = '';
  get accessToken(): string {
    return this._accessToken;
  }

  private revokeAccessToken: string = '';
  public fullScopes: boolean = false;

  // Observable subjects for auth state management
  public initDone: ReplaySubject<any> = new ReplaySubject();
  public loggedIn: ReplaySubject<any> = new ReplaySubject();
  public loggedOut: ReplaySubject<any> = new ReplaySubject();
  public basicLoggedIn: ReplaySubject<any> = new ReplaySubject();

  constructor(
    private platform: Platform,
    private storage: StorageService,
    private api: ApiService,
    private utils: UtilsService
  ) {
    // Dev uses the teamtools-dev project (995480605258), which is in "test" mode so it accepts non-https redirects. See README.
    if (this.utils.environment === 'dev') {
      this.G_CLIENT_ID = '995480605258-fojdbpcdigt1hak230p3t1l7ehde64as.apps.googleusercontent.com';
      this.G_CLIENT_SECRET = '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';
    }

    // Set up logout handling
    this.loggedOut.subscribe(() => {
      this.loggedOutActions();
    });
  }

  // Initialize Google authentication service
  public async initialize(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      return this.nativeInitialize();
    } else {
      return this.webInitialize();
    }
  }

  // Initialize Google authentication service for native platforms
  private async nativeInitialize(): Promise<void> {
    try {
      this.utils.logInfo('initializing google: native');
      
      // Initialize SocialLogin plugin
      await SocialLogin.initialize({
        google: {
          webClientId: this.G_CLIENT_ID,
          mode: 'offline'
        }
      });
      
      this.initDone.next(true);
      
      // Try silent login (auto-login)
      try {
        this.utils.logInfo('attempting native silent login');
        const result = await SocialLogin.login({
          provider: 'google',
          options: {
            scopes: this.G_SCOPES.split(' ').filter(s => s.trim()),
            forceRefreshToken: true
          }
        });
        
        this.utils.logInfo('user auto-login: native');
        this.gAuthObj = (result as any).result || result;
        await this.nativeGetExtraInfo((result as any).result || result);
        this.logUserIn();
      } catch (error) {
        this.utils.logInfo('error after trySilentLogin', error);
        
        // If offline, load local team if there
        if (this.utils.offline) {
          const team = await this.storage.get('google-team');
          this.utils.logDebug('local team', team);
          
          if (team && team.members && team.members.length > 0) {
            this.utils.logInfo('locally stored team data, logging user in');
            this.loggedIn.next(true);
          } else {
            this.utils.logInfo('no locally stored team data, logging user out');
            this.loggedOut.next(true);
          }
        } else {
          this.loggedOut.next(true);
        }
      }
    } catch (error) {
      this.utils.logInfo('google native init failed', error);
      this.utils.handleError('TeamTools cannot initialize Google services. Please contact TeamTools support.');
      this.loggedOut.next(true);
    }
  }

  // Initialize Google authentication service for web
  private async webInitialize(): Promise<void> {
    // Wait for Google libraries to load
    if (typeof google === 'undefined' || !_.has(google, 'accounts')) { // _.isUndefined(gapi) didn't work; maybe due to ts declaration?
      this.utils.logInfo('initialize google: waiting, google does not exist yet');
      setTimeout(() => {
        this.initialize();
      }, 500);
      return Promise.resolve();
    }

    this.utils.logInfo('initializing google: web');
    this.webInitAuthService();
    this.initDone.next(true);

    // Check for existing authentication
    try {
      this.utils.logInfo('checking for locally stored id info');
      
      const gAuthObjStr = await this.storage.get(this.G_USER_INFO_NAME);
      if (gAuthObjStr && gAuthObjStr !== '') {
        const storedAuthObj = this.decodeAuth(gAuthObjStr);
        this.utils.logDebug('stored gAuthObj: ', storedAuthObj);
        this.utils.logInfo('locally stored id info, refreshing tokens');
        
        this.gAuthObj = storedAuthObj;
        this.fullScopes = storedAuthObj.fullScopes;

        if (this.utils.offline) {
          // Check for offline team data
          const team = await this.storage.get('google-team');
          this.utils.logDebug('local team', team);
          
          if (team && team.members && team.members.length > 0) {
            this.utils.logInfo('locally stored team data, logging user in');
            this.logUserIn();
          } else {
            this.utils.logInfo('no locally stored team data, logging user out');
            this.loggedOut.next(true);
          }
        } else {
          // Online - refresh tokens and log in
          await this.refreshTokens();
          this.utils.logInfo('tokens refreshed after init, logging user in');
          this.logUserIn();
        }
      } else {
        this.utils.logInfo('no locally stored refresh token after init, logging user out');
        this.loggedOut.next(true);
      }
    } catch (error) {
      this.utils.logInfo('google web init failed', error);
      this.utils.handleError('TeamTools cannot initialize Google services. Please contact TeamTools support.');
      this.loggedOut.next(true);
    }
  }

  // Initialize the Google OAuth service for web
  private webInitAuthService(): void {
    // see https://developers.google.com/identity/oauth2/web/reference/js-reference#google.accounts.oauth2.initCodeClient
    this.gAuthService = google.accounts.oauth2.initCodeClient({
      client_id: this.G_CLIENT_ID,
      scope: this.fullScopes ? this.G_SCOPES : this.G_SCOPES_MINIMAL,
      select_account: true,
      hint: this.email,
      // hosted_domain: 'ibfw.org',
      callback: async (codeResponse: any) => {
        this.utils.logInfo('google requestCode returned', codeResponse);

        // Handle errors
        if (!codeResponse || _.has(codeResponse, 'error_description') || _.has(codeResponse, 'error')) {
          this.fullScopes = false;
          if (!codeResponse) {
            this.utils.logInfo('requestCode callback missing codeResponse', codeResponse);
            this.utils.handleError(this.LOGIN_ERROR_MSG);
            return;
          } else if (_.has(codeResponse, 'error') && codeResponse.error === 'access_denied') {
            this.utils.logInfo('user canceled popup', codeResponse);
            return;
          }
          // below is per spec, but it never gets called, instead figured out the above from trial and error and provided error callback below
          // else if (codeResponse.error_description === 'popup_closed') {
          //   this.utils.logInfo('user closed popup', codeResponse);
          // } else if (codeResponse.error_description === 'popup_failed_to_open') {
          //   this.utils.logInfo('user popup open failed', codeResponse);
          //   this.utils.handleError('Google login popup blocked. Please disable popup blocker.');
          // } else {
          //   this.utils.logInfo('requestCode unknown error_description', codeResponse);
          //   this.utils.handleError(this.LOGIN_ERROR_MSG);
          // }
        } else if (this.fullScopes && !this.utils.includesWords((codeResponse as any).scope, this.G_SCOPES)) {
          this.utils.logInfo('user did not grant all scopes', codeResponse);
          this.fullScopes = false;
          return;
        }

        try {
          // Get tokens and user info
          await this.webGetExtraInfo(codeResponse);
          
          if (this.fullScopes) {
            this.loggedInActions();
            this.basicLoggedIn.next(true);
          } else {
            this.logUserIn();
            // Set fullScopes based on granted permissions; do here so that it will be used in refreshTokens and logout
            if (this.utils.includesWords((codeResponse as any).scope, this.G_SCOPES)) {
              this.utils.logInfo('user logged in with full scopes', codeResponse);
              this.fullScopes = true;
            }
          }
        } catch (error) {
          this.utils.logInfo('error caught in login callback after requestCode', error);
          const errorStr = _.toString(error);
          
          if (_.includes(errorStr, 'gmail')) {
            // error out for gmail login
            // the below reloades the page
            this.utils.handleError(this.NO_GMAIL_MSG);
          } else if (!_.has(error, 'error') || error.error !== 'popup_closed_by_user') {
            // ignore popup closed by user, which is ok
            this.utils.handleError(this.LOGIN_ERROR_MSG);
          }
          
          if (!this.fullScopes) {
            this.loggedOut.next(true);
          }
        }
      },
      // this is called for closed popup
      error_callback: (error: any) => {
        this.fullScopes = false;
        if (!error || !_.has(error, 'type')) {
          this.utils.logInfo('requestCode error_callback missing type', error);
          this.utils.handleError(this.LOGIN_ERROR_MSG);
        } else if (error.type === 'popup_closed') {
          this.utils.logInfo('user closed popup', error);
        } else if (error.type === 'popup_failed_to_open') {
          this.utils.logInfo('user popup open failed', error);
          this.utils.handleError('Google login popup blocked. Please disable popup blocker.');
        } else {
          this.utils.logInfo('requestCode unknown error_callback', error);
          this.utils.handleError(this.LOGIN_ERROR_MSG);
        }
      }
    });
  }

  private webGetExtraInfo(codeResponse: any) {
    // https://developers.google.com/identity/oauth2/web/reference/js-reference#google.accounts.oauth2.initCodeClient
    if (!codeResponse || !_.has(codeResponse, 'code') || codeResponse.code === '') {
      return Promise.reject('error retrieving code from codeResponse');
    }
    // https://developers.google.com/identity/protocols/oauth2/web-server#exchange-authorization-code
    this.utils.logInfo('trying to get access token from authCode');
    // from https://github.com/manfredsteyer/angular-oauth2-oidc/blob/e28e14b222927f4f1592cd442534dc27361a815e/angular-oauth2-oidc/src/oauth-service.ts
    let search = new URLSearchParams();
    search.set('code', codeResponse.code);
    search.set('client_id', this.G_CLIENT_ID);
    search.set('client_secret', this.G_CLIENT_SECRET);
    if (this.utils.environment === 'dev') {
      // search.set('redirect_uri', 'http://dev.teamtoolsapp.com:8100');
      search.set('redirect_uri', 'postmessage');
    } else {
      // search.set('redirect_uri', 'https://app.teamtoolsapp.com');
      search.set('redirect_uri', 'postmessage');
    }  
    search.set('grant_type', 'authorization_code');

    let body = search.toString();

    let headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    let options: any = { headers: headers };    
    return this.api.post('https://www.googleapis.com', 'oauth2/v4/token', body, options).then((res: any) => {
      this.utils.logDebug('google token response:', res);
      if (!res || !_.has(res, 'access_token') || res.access_token === '' || !_.has(res, 'id_token') || res.id_token === '') {
        this.utils.logError('google token response was missing access or id token', res);
        return Promise.reject('google token response was missing access or id token');
      } else if (!res || !_.has(res, 'refresh_token') || res.refresh_token === '') {
          this.utils.logError('google token response was missing a refresh token', res);
          return Promise.reject('google token response was missing a refresh token');
      } else {
        this.gAuthObj = {};
        this.gAuthObj['fullScopes'] = this.fullScopes;
        this.gAuthObj['accessToken'] = this.fullScopes ? res.access_token : ''; // only put in for basic mode
        this.revokeAccessToken = res.access_token;
        this.gAuthObj['idToken'] = res.id_token;
        this.gAuthObj['refreshToken'] = res.refresh_token;
        // set logged in time for expiration
        this.gAuthObj['loggedInTime'] = Date.now();
        return Promise.resolve('retrieved tokens');
      }
    }).then(() => {
      return this.webGetIdtokenInfo();
    }).then(() => {
      let authObjRedacted: any = {};
      this.utils.populatePropertiesFromObj(authObjRedacted, this.gAuthObj);
      authObjRedacted.accessToken = '';
      authObjRedacted.idToken = '';
      return this.storage.set(this.G_USER_INFO_NAME, this.encodeAuth(authObjRedacted));
    }).catch((err: any) => {
      this.utils.logDebug('error details:', err);
      this.utils.logDebug('error string:', _.toString(err));
      this.utils.logError('error retrieving tokens from authorization code', err);
      return Promise.reject('error retrieving tokens from authorization code: '+ err);
    });  
  }

  private webGetIdtokenInfo() {
    // https://www.googleapis.com/oauth2/v3/tokeninfo?id_token=**_**XYZ123_, see https://github.com/EddyVerbruggen/cordova-plugin-googleplus/pull/303
    let params = new HttpParams().set('id_token', this.gAuthObj.idToken);
    return this.api.get('https://www.googleapis.com', 'oauth2/v3/tokeninfo', params).then((res: any) => {
      if (!_.has(res, 'hd') || res.hd === '' || (_.has(res, 'email') && this.isGmailAddress(res.email))) {
        this.utils.logError('hd missing or gmail address', res);
        return Promise.reject('google tokeninfo response was missing hd or had gmail address');        
      } else if (_.has(res, 'hd') && res.hd !== '' && _.has(res, 'sub') && res.sub !== '' && _.has(res, 'email') && res.email !== '' && _.has(res, 'name') && res.name !== '') {
        this.gAuthObj['userId'] = res.sub;
        this.gAuthObj['email'] = res.email;
        this.gAuthObj['displayName'] = res.name;
        this.gAuthObj['familyName'] = res.family_name;
        this.gAuthObj['givenName'] = res.given_name;
        this.gAuthObj['imageUrl'] = res.picture;
        this.gAuthObj['hostedDomain'] = res.hd;
        return Promise.resolve('retrieved user info');
      } else {
        this.utils.logError('id token was missing a field', res);
        return Promise.reject('google tokeninfo response was missing a field');
      }
    });
  }

  // this probably needs work, check against googleT2 code
  private async nativeGetExtraInfo(authResult: any): Promise<void> {
    try {
      // Extract user info from native auth result
      if (authResult && authResult.email && authResult.id) {
        // Check for Gmail addresses
        if (this.isGmailAddress(authResult.email)) {
          return Promise.reject('gmail not allowed');
        }

        // Check for hosted domain (Google Workspace)
        const emailParts = authResult.email.split('@');
        if (emailParts.length !== 2 || emailParts[1] === 'gmail.com' || emailParts[1] === 'googlemail.com') {
          this.utils.logError('google auth response had no hosted domain or was gmail');
          return Promise.reject('google auth response had no hosted domain');
        }

        // Populate auth object with native response data
        this.gAuthObj['userId'] = authResult.id;
        this.gAuthObj['email'] = authResult.email;
        this.gAuthObj['displayName'] = authResult.name || '';
        this.gAuthObj['familyName'] = authResult.familyName || '';
        this.gAuthObj['givenName'] = authResult.givenName || '';
        this.gAuthObj['imageUrl'] = authResult.imageUrl || '';
        this.gAuthObj['hostedDomain'] = emailParts[1];
        this.gAuthObj['loggedInTime'] = Date.now();

        // Set tokens if available
        if (authResult.accessToken) {
          this.gAuthObj['accessToken'] = authResult.accessToken;
        }
        if (authResult.idToken) {
          this.gAuthObj['idToken'] = authResult.idToken;
        }
        if (authResult.refreshToken) {
          this.gAuthObj['refreshToken'] = authResult.refreshToken;
        }

        return Promise.resolve();
      } else {
        this.utils.logError('native auth result was missing required fields', authResult);
        return Promise.reject('native auth result was missing required fields');
      }
    } catch (error) {
      this.utils.logError('Error processing native auth info', error);
      return Promise.reject('Could not process native auth info');
    }
  }

  // called from login screen page upon initDone
  public attachLoginToElement(element: any) {
    if (Capacitor.isNativePlatform()) {
      this.nativeAttachLoginToElement(element);
    } else {
      this.webAttachLoginToElement(element);
    }
  }

  private nativeAttachLoginToElement(element: any) {
    this.utils.logInfo('attaching native login action to button');
    if (element) {
      element.onclick = async () => {
        if (this.utils.offline) {
          this.utils.handleError('No network connection!');
          return;
        }

        try {
          const result = await SocialLogin.login({
            provider: 'google',
            options: {
              scopes: this.G_SCOPES.split(' ').filter(s => s.trim()),
              forceRefreshToken: true
            }
          });

          this.utils.logInfo('user logged in via native');
          this.utils.logDebug('native login result', result);

          const authData = (result as any).result || result;
          if (this.isGmailAddress(authData.email)) {
            this.utils.handleError(this.NO_GMAIL_MSG);
            this.loggedOut.next(true); // so they are not auto-logged in with gmail acct next time
            return;
          }

          this.gAuthObj = authData;
          await this.nativeGetExtraInfo(authData);
          this.logUserIn();
        } catch (error) {
          this.utils.handleError(error);
        }
      };
    }
  }

  private webAttachLoginToElement(element: any) {
    this.utils.logInfo('attaching web login action to button');
    if (element) {
      element.onclick = (e: any) => {
        // google.accounts.id.initialize({
        //   client_id: this.G_CLIENT_ID,
        //   callback: (response) => {
        //     that.gAuthService.requestCode();
        //   }
        // });
        // google.accounts.id.prompt();
        this.gAuthService.requestCode();
      };  
    }
  } 

  // called from setup screen page upon try basic
  public basicLogin() {
    this.fullScopes = true;
    if (Capacitor.isNativePlatform()) {
      this.nativeBasicLogin();
    } else {
      this.webBasicLogin();
    }
  }

  private async nativeBasicLogin() {
    try {
      const result = await SocialLogin.login({
        provider: 'google',
        options: {
          scopes: this.G_SCOPES.split(' ').filter(s => s.trim()),
          forceRefreshToken: true
        }
      });

      this.gAuthObj = (result as any).result || result;
      await this.nativeGetExtraInfo((result as any).result || result);
      this.loggedInActions();
      this.basicLoggedIn.next(true);
    } catch (error) {
      this.utils.logError('Native basic login failed', error);
      this.utils.handleError(this.LOGIN_ERROR_MSG);
    }
  }

  private webBasicLogin() {
    this.webInitAuthService();
    this.gAuthService.requestCode();
  }

  // Internal method to log user in after authentication
  private logUserIn(): void {
    this.utils.logInfo('logUserIn called');
    this.loggedInActions();

    // Check for Gmail accounts
    if (this.isGmailAddress(this.email)) {
      this.utils.handleError('TeamTools is for Google Workspace users, please log in with your Google Workspace email!');
    } else {
      this.loggedIn.next(true);
    }
  }

  // called upon loggedIn, either due to auto-login or user login
  private loggedInActions() {
    this.utils.logInfo('loggedInActions called');
    if (this.utils.offline) {
      // do nothing
    } else {
      // for web client: see https://developers.google.com/identity/sign-in/web/reference
      // see https://developers.google.com/identity/sign-in/web/reference#googleusergetid
      // for native client: see https://github.com/EddyVerbruggen/cordova-plugin-googleplus
      // this.utils.logDebug('gAuthObj = '+JSON.stringify(this.gAuthObj));
      this.id = this.gAuthObj.userId;
      this.email = this.gAuthObj.email;
      this.displayName = this.gAuthObj.displayName;
      this.lastName = this.gAuthObj.familyName;
      this.firstName = this.gAuthObj.givenName;
      this.imageUrl = this.gAuthObj.imageUrl; 
      this.domain = this.gAuthObj.hostedDomain;
      this.loggedInTime = this.gAuthObj.loggedInTime;
      this._idToken = this.gAuthObj.idToken;
      this._accessToken = this.gAuthObj.accessToken;
      // this.fullScopes = this.gAuthObj.fullScopes;
    } 

    // set user in utils for logging
    this.utils.logDebug('google: performed logged in actions for '+this.email);
    this.utils.userEmail = this.email;
  }

  public loggedOutActions() {
    if (Capacitor.isNativePlatform()) {
      this.nativeLoggedOutActions();
    } else {
      this.webLoggedOutActions();
    }
  }

  private async nativeLoggedOutActions() {
    this.utils.logDebug('google: performing native logged out actions');
    if (this.utils.offline) {
      // do nothing
    } else {
      try {
        // Native logout using SocialLogin
        await SocialLogin.logout({ provider: 'google' });
        this.utils.logDebug('native logged out');
      } catch (error) {
        this.utils.logInfo('error during native logout', error);
      }
    }
  }

  private webLoggedOutActions() {
    this.utils.logDebug('google: performing web logged out actions');
    if (this.utils.offline) {
      // do nothing
    } else {
      this.storage.remove(this.G_USER_INFO_NAME).then(() => {
        if (this.shouldRevokeAccess()) {
          this.revokeScopes(); // do for everyone to catch basic mode users who previously logged in in a different browser
        }
      }).catch((reason: any) => {
        this.utils.logInfo('error deleting local user info', reason);
      });
    }
  }

  // for use in calling refresh with force, as well as with edit calls to server
  public refreshTokens(fromOffline?: boolean): Promise<any> {
    if (Capacitor.isNativePlatform()) {
      return this.nativeRefreshTokens(fromOffline);
    } else {
      return this.webRefreshTokens(fromOffline);
    }
  }

  private async nativeRefreshTokens(fromOffline?: boolean): Promise<any> {
    try {
      this.utils.logDebug('refreshing native tokens');
      
      // Use SocialLogin to refresh tokens
      const result = await SocialLogin.login({
        provider: 'google',
        options: {
          scopes: this.G_SCOPES.split(' ').filter(s => s.trim()),
          forceRefreshToken: true
        }
      });

      this.gAuthObj = (result as any).result || result;
      await this.nativeGetExtraInfo((result as any).result || result);

      if (fromOffline) {
        this.logUserIn(); // needed to populate user if not already populated
      } else {
        this.utils.logDebug('calling loggedInActions');
        this.loggedInActions();
      }

      return this._accessToken;
    } catch (error) {
      this.utils.logError('native token refresh failed', error);
      return Promise.reject(error);
    }
  }

  private webRefreshTokens(fromOffline?: boolean): Promise<any> {
    this.utils.logDebug('refreshing tokens');
    let search = new URLSearchParams();
    search.set('refresh_token', this.gAuthObj.refreshToken);
    search.set('client_id', this.G_CLIENT_ID);
    search.set('client_secret', this.G_CLIENT_SECRET);
    if (this.utils.environment === 'dev') {
      // search.set('redirect_uri', 'http://dev.teamtoolsapp.com:8100');
      search.set('redirect_uri', 'postmessage');
    } else {
      // search.set('redirect_uri', 'https://app.teamtoolsapp.com');
      search.set('redirect_uri', 'postmessage');
    }  
    search.set('grant_type', 'refresh_token');

    let body = search.toString();

    let headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    let options: any = { headers: headers };    
    return this.api.post('https://www.googleapis.com', 'oauth2/v4/token', body, options).then((res: any) => {
      this.utils.logDebug('google refreshed tokens response:', res);
      if (!res || !_.has(res, 'access_token') || res.access_token === '' || !_.has(res, 'id_token') || res.id_token === '') {
        this.utils.logError('google token response was missing access or id token', res);
        return Promise.reject('google token response was missing access or id token');
      } else {
        this.gAuthObj['accessToken'] = this.fullScopes ? res.access_token : ''; // only set for basicmode
        this.revokeAccessToken = res.access_token;
        this.gAuthObj['idToken'] = res.id_token;
        return Promise.resolve();
      }
    }).then(() => { 
      this.utils.logDebug('back from getting new tokens');
      this.utils.logDebug('calling loggedInActions');
      this.loggedInActions();
      return this.gAuthObj['accessToken'];
    });  
  }

  // helper method to determine if we should revoke access
  private shouldRevokeAccess(): boolean {
    // Check if we have a valid auth object and access token to revoke
    return this.gAuthObj &&
           typeof this.gAuthObj === 'object' &&
           Object.keys(this.gAuthObj).length > 0;
  }

  // called upon logout above
  public revokeScopes() {
    this.utils.logDebug('revoking scopes');
    if (this.revokeAccessToken && this.revokeAccessToken.trim() !== '') {
      google.accounts.oauth2.revoke(this.revokeAccessToken, () => {
        this.utils.logDebug('revoked scopes');
        // reset everything in case another login happens before page refresh
        this.fullScopes = false;
        this.webInitAuthService();
      });
    } else {
      this.utils.logDebug('no valid access token to revoke, skipping revocation');
      // still reset auth service
      this.fullScopes = false;
      this.webInitAuthService();
    }
  }

  public isGmailAddress(address: string) {
    return _.includes(address, '@gmail.com') || _.includes(address, '@googlemail.com');
  }

  public encodeAuth(authObj: any) {
    let tempAuthObj: any = {};
    this.utils.populatePropertiesFromObj(tempAuthObj, authObj);
    tempAuthObj['id'] = authObj.refreshToken;
    _.unset(tempAuthObj, 'refreshToken');
    return this.utils.encodeObj(tempAuthObj);
  }

  public decodeAuth(authObjStr: string) {
    let authObj: any = this.utils.decodeObj(authObjStr);
    authObj['refreshToken'] = authObj.id;
    _.unset(authObj, 'id');
    return authObj;
  }
}